import { useRef, useState } from 'react';

import Link from 'next/link';
import { useEffectOnce } from 'usehooks-ts';

import {
  FilterRequestObject,
  SearchRequestObject,
} from '@/api/elasticsearch/models';
import { searchElasticApi } from '@/api/fetch/rest-search-api';
import {
  CarouselEvents,
  ClassifiedPageEvents,
} from '@/modules/analytics/event-types';
import useRudderTrack from '@/modules/analytics/hooks/useRudderTrack';
import { CarouselType } from '@/modules/analytics/types';
import Text from '@/tayara-kit/Text';
import XCarousel from '@/tayara-kit/XCarousel';

import { IListingItem, IUserData } from '../../types';
import CompactClassifiedCard from '../RecentClassifiedsList/CompactClassifiedCard';

interface MoreFromUserProps {
  userData: IUserData;
  viewedClassifiedId: string;
}

const MoreFromUser: React.FC<MoreFromUserProps> = ({
  userData,
  viewedClassifiedId,
}) => {
  const [userClassifieds, setUserClassifieds] = useState<IListingItem[]>([]);
  const parentRef = useRef<HTMLDivElement>(null);
  const { rudderTrack, trackCarouselClassifiedClick } = useRudderTrack();

  useEffectOnce(() => {
    const filterRequest: FilterRequestObject = {
      categoryId: '',
      subCategoryId: '',
      adParamsMap: {},
      rangeAdParamsMap: {},
      governorate: '',
      delegation: [],
      minPrice: 0,
      maxPrice: 0,
      productTypeList: [],
      level: 0,
      state: 2,
      userid: userData.kcid!,
    };
    const searchRequest: SearchRequestObject = {
      query: '',
      offset: 0,
      limit: 20,
      sort: 0,
      filter: filterRequest,
    };

    searchElasticApi(searchRequest, false).then(([[newHits, _]]) => {
      setUserClassifieds(
        newHits.filter((item) => item.id !== viewedClassifiedId)
      );
    });
  });

  return (
    <>
      <div className="my-10">
        <div className="flex items-center">
          <Text
            as="h2"
            text="Vous trouverez aussi chez "
            className="text-gray-700 font-semibold text-lg my-4"
          />
          <Link
            href={`/shop/${userData.kcid}`}
            onClick={() =>
              rudderTrack(ClassifiedPageEvents.CLICK_AVATAR_MORE_FROM_USER, {
                ...userData,
                user_id: userData.id,
              })
            }
            className="rounded-lg flex ml-2 p-2 gap-x-2 items-center hover:bg-neutral-100"
          >
            <img
              className="mask mask-squircle w-7 h-7"
              src={userData.avatar}
              alt={`Shop's avatar of ${userData.fullname} on tayara`}
            />

            <p className="text-sm font-bold underline hover:no-underline text-neutral-600 capitalize">
              {userData.fullname?.toLocaleLowerCase()}
            </p>
          </Link>
        </div>
        <div ref={parentRef} className="relative pr-6">
          <XCarousel
            portalEl={parentRef.current}
            /* onStopScroll={(progress) => {
            trackCarouselScroll(CarouselEvents.SCROLL_CAROUSEL, {
              scroll_percentage: progress,
              carousel_type: CarouselType.MORE_FROM_USER,
              relative_data: null,
            });
          }} */
          >
            <ul className="h-full flex gap-px pt-1">
              {userClassifieds.length > 0 ? (
                userClassifieds.map((data, index) => (
                  <li key={index}>
                    <CompactClassifiedCard
                      onClick={() =>
                        trackCarouselClassifiedClick(
                          CarouselEvents.CLICK_CLASSIFIED,
                          {
                            carousel_type: CarouselType.SHOWCASED_CATEGORY,
                            classified_details: data,
                            classified_position: index,
                          }
                        )
                      }
                      classifiedData={data}
                    />
                  </li>
                ))
              ) : (
                <p className="m-auto font-extrabold my-11 text-2xl text-neutral-200">
                  {userData.name} n&apos;a pas d&apos;autres annonces actives
                </p>
              )}
            </ul>
          </XCarousel>
        </div>
      </div>
    </>
  );
};

export default MoreFromUser;
